import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { cn } from "@/src/lib/utils";

import SelectBoxCheckBox from "./SelectBoxCheckbox";
import { SelectBoxContext } from "./SelectBoxContext";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

import type { JSX } from "react";

import type { SelectBoxTab } from "./selectBox.types";

const TAB_LABEL_KEY_MAP = {
  contents: "tabContents",
  standard: "tabSmartlists",
  audios: "tabAudios",
  displays: "tabDisplays",
  castings: "tabCastings",
  series: "tabSeries",
} as const;

type TabLabelKey = keyof typeof TAB_LABEL_KEY_MAP;

function isTabLabelKey(key: string): key is TabLabelKey {
  return key in TAB_LABEL_KEY_MAP;
}

interface SelectBoxNavTabsProps {
  tab_index: number;
  changeTab: (value: number) => void;
  selected?: boolean;
  tabs: SelectBoxTab[];
  canCheck: boolean;
  isChecked?: boolean;
}

function SelectBoxNavTabs({
  tab_index,
  changeTab,
  selected,
  tabs,
  canCheck,
  isChecked,
}: SelectBoxNavTabsProps): JSX.Element {
  const { t } = useTranslation("editor-utils");
  const context = useContext(SelectBoxContext);
  const {
    libraryFilteredItems,
    selectedFilteredItems,
    setMultiSelectList,
    setSelectedTab,
  } = context;
  const [isAllChecked, setIsAllChecked] = useState(false);

  useEffect(() => {
    if (!selected) setSelectedTab(tabs[0]);
  }, [selected, setSelectedTab, tabs]);

  useEffect(() => {
    setIsAllChecked(false);
    setMultiSelectList([]);
  }, [setMultiSelectList, tab_index]);

  useEffect(() => {
    setIsAllChecked((currentIsAllChecked) => isChecked ?? currentIsAllChecked);
  }, [isChecked]);

  const onChangeIsAllChecked = useCallback(
    (allChecked: boolean): void => {
      const list = !selected ? libraryFilteredItems : selectedFilteredItems;

      const sourceId = selected ? "droppableSelected" : "droppableLibrary";

      list.forEach((item) => {
        if (item.isDraggable) {
          // Keep existing behavior: checked state is stored directly on selectbox items.
          // eslint-disable-next-line no-param-reassign
          item.source_id = sourceId;
          // eslint-disable-next-line no-param-reassign
          item.isChecked = allChecked;
        }
      });

      setMultiSelectList(allChecked ? list : []);
      setIsAllChecked(allChecked);
    },
    [libraryFilteredItems, selected, selectedFilteredItems, setMultiSelectList],
  );

  const handleChangeTab = useCallback(
    (value: string): void => changeTab(Number(value)),
    [changeTab],
  );

  const isSingleTab = tabs.length === 1;

  const tabsTriggerClassNames = cn(
    "min-w-0 flex-1 flex-col gap-0.5 px-1 py-1",
    isSingleTab ? "after:bg-transparent" : "after:bg-primary",
  );

  return (
    <div className="selectbox-nav-tabs-container">
      <Tabs onValueChange={handleChangeTab} value={tab_index.toString()}>
        <TabsList className="h-14! w-full gap-0" variant="line">
          {tabs.map((tab) => {
            const labelKey = isTabLabelKey(tab.key)
              ? TAB_LABEL_KEY_MAP[tab.key]
              : undefined;

            return (
              <TabsTrigger
                key={tab.key}
                className={tabsTriggerClassNames}
                value={tab.index.toString()}
              >
                <i className="material-icons text-xl! leading-none">
                  {tab.icon}
                </i>
                {labelKey ? (
                  <span className="w-full truncate text-[0.6875rem] leading-tight">
                    {t(labelKey)}
                  </span>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
      <div className="absolute top-1/2 right-0 -translate-x-1/2 -translate-y-1/2">
        <SelectBoxCheckBox
          key="selectbox-tab-container-checkbox"
          canCheck={canCheck}
          isChecked={isAllChecked}
          onCheckedChange={onChangeIsAllChecked}
        />
      </div>
    </div>
  );
}

export default memo(SelectBoxNavTabs);
